import { Button, Flex, Input, Modal, Select, Space } from 'antd';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import type { Return as Fachgebiet } from '../../../@types/native-api/generated/basev17';
import type { Return1 as Behandlungsfall, Return2 as SVT } from '../../../@types/native-api/generated/fdasv1';
import { LoadingIndicator } from '../../../components/LoadingIndicator.tsx';
import { ecardEnv, getCardSlot } from '../../../utils/ginoHelpers.ts';
import type { EcardConfig } from './EcardConfigs.tsx';

export const EcardDebuggerModal: React.FC<{ config: EcardConfig | null; onClose: () => void }> = ({ config, onClose }) => {
  const [dialogId, setDialogId] = useState('');
  const [svnr, setSvnr] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [fachgebiete, setFachgebiete] = useState<Fachgebiet[]>([]);
  const [fachgebietId, setFachgebietId] = useState<string | null>();
  const [behandlungsfaelle, setBehandlungsfaelle] = useState<Behandlungsfall[]>([]);
  const [behandlungsfallId, setBehandlungsfallId] = useState<string | null>();
  const [svts, setSvts] = useState<SVT[]>([]);
  const [svtId, setSvtId] = useState<string | null>(null);
  const [dialogAddressInitialized, setDialogAddressInitialized] = useState(false);
  const [konsultationId, setKonsultationId] = useState<number>(0);
  const auth = useAuth();

  useEffect(() => {
    if (!dialogId || !config || !dialogAddressInitialized) {
      return;
    }
    const fetch = async () => {
      const response = await window.nativeApi?.ecardFachgebieteByOrd(ecardEnv(), {
        dialogId: dialogId,
        ordId: config?.ordinationId ?? '',
        taetigkeitsBereichId: config?.taetigkeitsBereichId ?? '',
      });
      setFachgebiete(response?.data?.return ?? []);
    };
    fetch();
  }, [config, dialogId, dialogAddressInitialized]);

  useEffect(() => {
    if (!fachgebietId) {
      return;
    }
    const fetch = async () => {
      const response = await window.nativeApi?.ecardBehandlungsfaelleByFachgebiet(ecardEnv(), {
        fachgebietCode: fachgebietId,
      });
      setBehandlungsfaelle(response?.data?.return ?? []);
    };
    fetch();
  }, [fachgebietId]);

  useEffect(() => {
    const fetch = async () => {
      const response = await window.nativeApi?.ecardSvts(ecardEnv());
      setSvts(response?.data?.return ?? []);
    };
    fetch();
  }, []);

  const fetchEcardInfo = async () => {
    setOutput('');
    setLoading(true);
    try {
      const response = await window.nativeApi!.ginoStatus(config?.cardReaderAddress ?? '', {
        returnEhicData: true,
        sigType: 'sv',
        vpnr: config?.vpnr ?? '',
      });

      if (!response.error) {
        setOutput(JSON.stringify(response.data, null, 2));
      } else {
        setOutput(JSON.stringify(response.error, null, 2));
      }
    } finally {
      setLoading(false);
    }
  };

  const createDialog = async () => {
    setOutput('');
    setLoading(true);
    try {
      const response = await window.nativeApi!.ginoStatus(config?.cardReaderAddress ?? '', {
        returnEhicData: true,
        sigType: 'vp',
        vpnr: config?.vpnr ?? '',
        pin: config?.cardPin ?? '',
      });

      if (!response.error) {
        const slot = getCardSlot(response, 'o-card');

        const resp = await window.nativeApi?.ecardCreateDialog(ecardEnv(), slot?.token?.value ?? '', auth.user?.profile.sub ?? 'unknown');

        if (resp?.error) {
          setOutput(JSON.stringify(resp.error, null, 2));
          return;
        } else {
          setOutput(JSON.stringify(resp?.data, null, 2));
          setDialogId(resp?.data?.return?.dialogId ?? '');
        }
      } else {
        setOutput(JSON.stringify(response.error, null, 2));
      }
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = useCallback(async () => {
    setOutput('');
    setLoading(true);
    setFachgebietId(null);
    setBehandlungsfallId(null);
    setDialogAddressInitialized(false);
    setKonsultationId(0);
    setSvtId(null);

    try {
      const response = await window.nativeApi?.ecardCloseDialog(ecardEnv(), dialogId);

      if (!response?.error) {
        setOutput(JSON.stringify(response?.data, null, 2));
      } else {
        setOutput(JSON.stringify(response?.error, null, 2));
      }
      setDialogId('');
    } finally {
      setLoading(false);
    }
  }, [dialogId]);

  const close = async () => {
    await closeDialog();
    onClose();
  };

  useEffect(() => {
    const listener = async () => {
      console.log('closeDialog beforeunload');
      await closeDialog();
    };
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  }, [closeDialog]);

  const setDialogAddress = async () => {
    setLoading(true);
    setOutput('');

    try {
      const response = await window.nativeApi?.ecardSetDialogAddress(ecardEnv(), {
        dialogId: dialogId,
        ordinationId: config?.ordinationId ?? '',
        taetigkeitsBereichId: config?.taetigkeitsBereichId ?? '',
      });

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setDialogAddressInitialized(true);
      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    setOutput('');

    try {
      const response = await window.nativeApi?.ecardServices(ecardEnv());

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const fetchFdas = async () => {
    setLoading(true);
    setOutput('');

    try {
      const fachgebiete = await window.nativeApi?.ecardFachgebiete(ecardEnv());
      const behandlungsfaelle = await window.nativeApi?.ecardBehandlungsfaelle(ecardEnv());
      const svts = await window.nativeApi?.ecardSvts(ecardEnv());
      const uvts = await window.nativeApi?.ecardUvts(ecardEnv());
      const staaten = await window.nativeApi?.ecardStaaten(ecardEnv());
      const staatenIso = await window.nativeApi?.ecardStaatenIso(ecardEnv());
      const behandlungsfaelleByFachgebiet50 = await window.nativeApi?.ecardBehandlungsfaelleByFachgebiet(ecardEnv(), {
        fachgebietCode: '50',
      });

      setOutput(
        JSON.stringify(
          {
            fachgebiete,
            behandlungsfaelle,
            svts,
            uvts,
            staaten,
            staatenIso,
            behandlungsfaelleByFachgebiet50,
          },
          null,
          2
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchReaders = async () => {
    setLoading(true);
    setOutput('');

    try {
      const response = await window.nativeApi?.ecardReaders(ecardEnv());

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const fetchBerechtigungen = async () => {
    setLoading(true);
    setOutput('');

    try {
      const response = await window.nativeApi?.ecardBerechtigungen(ecardEnv(), dialogId);

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientendaten = async () => {
    setLoading(true);
    setOutput('');

    try {
      const response = await window.nativeApi?.ecardPatientendaten(ecardEnv(), {
        dialogId: dialogId,
        svNummer: svnr,
      });

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const fetchAdressdaten = async () => {
    setLoading(true);
    setOutput('');

    try {
      const response = await window.nativeApi?.ecardAdressdaten(ecardEnv(), {
        dialogId: dialogId,
        svNummer: svnr,
      });

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const fetchVersichertendatenSvnr = async () => {
    setLoading(true);
    setOutput('');

    try {
      const response = await window.nativeApi?.ecardVersichertendaten(ecardEnv(), {
        dialogId: dialogId,
        suchKriterien: {
          svNummer: svnr,
        },
      });

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const fetchVersichertendatenToken = async () => {
    setLoading(true);
    setOutput('');

    let token = '';
    try {
      const response = await window.nativeApi!.ginoStatus(config?.cardReaderAddress ?? '', {
        returnEhicData: true,
        sigType: 'sv',
        vpnr: config?.vpnr ?? '',
        returnToken: true,
      });

      if (response.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }
      token = getCardSlot(response, 'e-card')?.token?.value ?? '';
    } catch (e) {
      setOutput(JSON.stringify(e, null, 2));
      return;
    } finally {
      setLoading(false);
    }

    if (!token) {
      setOutput('Kein e-card Token vorhanden');
      return;
    }

    setLoading(true);
    try {
      const response = await window.nativeApi?.ecardVersichertendaten(ecardEnv(), {
        dialogId: dialogId,
        cardToken: token,
      });

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const doKonsultationOcard = async () => {
    setLoading(true);
    setOutput('');

    const r = await window.nativeApi!.ginoStatus(config?.cardReaderAddress ?? '', {
      returnEhicData: true,
      sigType: 'sv',
      //vpnr: config?.vpnr ?? '',
      //pin: config?.cardPin ?? '',
      svnr: svnr,
      returnToken: true,
    });

    const slot = getCardSlot(r, 'o-card');

    try {
      const response = await window.nativeApi?.ecardDoKonsultation(ecardEnv(), {
        dialogId: dialogId,
        svNummer: svnr,
        svtCode: svtId ?? undefined,
        fachgebietsCode: fachgebietId ?? '',
        behandlungsfallCode: behandlungsfallId ?? '',
        cardToken: slot?.token?.value ?? '',
        isOffline: false,
      });

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setKonsultationId(response?.data?.return?.ergebnisKonsultation?.konsultationsDaten?.konsultationsBeleg?.id ?? 0);
      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  const stornoKonsultation = async () => {
    setLoading(true);
    setOutput('');

    try {
      const response = await window.nativeApi?.ecardStornoKonsultation(ecardEnv(), {
        dialogId: dialogId,
        konsId: konsultationId ?? '',
        konsVersion: 1,
      });

      if (response?.error) {
        setOutput(JSON.stringify(response.error, null, 2));
        return;
      }

      setOutput(JSON.stringify(response?.data, null, 2));
    } finally {
      setLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear the previous output whenever the config changes
  useEffect(() => {
    setOutput('');
  }, [config]);

  return (
    <Modal
      title="e-card Debugger"
      open={!!config}
      onCancel={close}
      width={600}
      onOk={close}
      cancelButtonProps={{ hidden: true }}
      okText="Schließen"
    >
      <Flex vertical gap="middle">
        <Button onClick={fetchServices}>Services abfragen</Button>
        <Button onClick={fetchFdas}>FDAS abfragen</Button>
        <Button onClick={fetchReaders}>Verfügbare GINOs abfragen</Button>
        <Button onClick={fetchEcardInfo}>GINO abfragen</Button>
        {dialogId ? (
          <Space orientation="vertical" style={{ width: '100%' }}>
            <span>
              Dialog ID: <strong>{dialogId}</strong>
            </span>
            <Space>
              <Button onClick={closeDialog}>Dialog beenden</Button>
              <Button onClick={setDialogAddress}>Dialog Standort/Adresse setzen</Button>
            </Space>
            <Select
              options={fachgebiete.map(it => ({
                label: it.text,
                value: it.code,
              }))}
              placeholder="Fachgebiet (Standort muss gesetzt sein)"
              style={{ width: '100%' }}
              value={fachgebietId}
              onChange={v => setFachgebietId(v)}
            />
            <Select
              options={behandlungsfaelle.map(it => ({
                label: it.baseProperty?.text,
                value: it.baseProperty?.code,
              }))}
              placeholder="Behandlungsfall (Fachgebiet muss gesetzt sein)"
              style={{ width: '100%' }}
              value={behandlungsfallId}
              onChange={v => setBehandlungsfallId(v)}
            />
            <Select
              options={svts.map(it => ({
                label: it.kurzbezeichnung,
                value: it.code,
              }))}
              placeholder="KV-Träger (Automatische Bestimmung, wenn nicht angegeben)"
              style={{ width: '100%' }}
              value={svtId}
              onChange={v => setSvtId(v)}
              allowClear
            />
          </Space>
        ) : (
          <Button onClick={createDialog}>Dialog aufbauen</Button>
        )}

        <Input placeholder="SVNR" value={svnr} onChange={e => setSvnr(e.target.value)} />
        <Button onClick={fetchBerechtigungen} disabled={!dialogId}>
          Berechtigungen abfragen
        </Button>
        <Button onClick={fetchPatientendaten} disabled={!svnr.trim() || !dialogId}>
          Patientendaten abfragen
        </Button>
        <Button onClick={fetchAdressdaten} disabled={!svnr.trim() || !dialogId}>
          Patientenadresse abfragen
        </Button>
        <Button onClick={fetchVersichertendatenSvnr} disabled={!svnr.trim() || !dialogId}>
          Versichertendaten mit SVNR abfragen
        </Button>
        <Button onClick={fetchVersichertendatenToken} disabled={!dialogId}>
          Versichertendaten mit e-card Token abfragen
        </Button>
        <Button onClick={doKonsultationOcard} disabled={!svnr.trim() || !dialogId || !fachgebietId || !behandlungsfallId}>
          Konsultation mit o-card
        </Button>
        <Button onClick={stornoKonsultation} disabled={!konsultationId}>
          Storno Konsultation
        </Button>

        {loading && <LoadingIndicator />}
        <pre style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>{output}</pre>
      </Flex>
    </Modal>
  );
};
